import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import * as React from "react";
import type { CalendarEvent, CalendarEventLabels } from "../types";

import { Metadata } from "../../metadata";
import { Attendee } from "../attendee";
import styles from "./DeclinedEventsPopover.module.css";

interface DeclinedEventItemProps {
  event: CalendarEvent;
  labels: Pick<
    CalendarEventLabels,
    "getNumAttendeesLabel" | "getFormattedEventTime"
  >;
}

const DeclinedEventItem = ({ event, labels }: DeclinedEventItemProps) => {
  const formattedTime = React.useMemo(() => {
    return labels.getFormattedEventTime({
      startTime: event.startTime,
      endTime: event.endTime,
      isAllDay: event.allDay,
    });
  }, [labels, event.startTime, event.endTime, event.allDay]);

  return (
    <div className={styles.eventItem}>
      <div className={styles.eventHeader}>
        <Text size="large" color="subtle" className={styles.strikethrough}>
          <Truncate tooltipControlProps={{ auto: true, placement: "top" }}>
            {event.title}
          </Truncate>
        </Text>

        <Metadata withDividers="bullet">
          <Metadata.Item>
            <Metadata.Label>
              <Text size="medium" color="subtle">
                {formattedTime}
              </Text>
            </Metadata.Label>
          </Metadata.Item>
          {event.attendees && event.attendees.length > 0 && (
            <Metadata.Item>
              <Attendee
                attendees={event.attendees}
                labels={{
                  getNumAttendeesLabel: labels.getNumAttendeesLabel,
                }}
                isSubtleLabel={true}
              />
            </Metadata.Item>
          )}
        </Metadata>
      </div>
    </div>
  );
};
DeclinedEventItem.displayName = "DeclinedEventItem";

export const DeclinedEventsPopover = ({
  declinedEvents,
  labels,
}: {
  declinedEvents: CalendarEvent[];
  labels: Pick<
    CalendarEventLabels,
    "getNumAttendeesLabel" | "getFormattedEventTime"
  >;
}) => {
  return (
    <Box className={styles.container}>
      <div className={styles.eventsContainer}>
        {declinedEvents.map((event, index) => (
          <React.Fragment key={event.id}>
            <DeclinedEventItem
              event={event}
              labels={{
                getNumAttendeesLabel: labels.getNumAttendeesLabel,
                getFormattedEventTime: labels.getFormattedEventTime,
              }}
            />
            {index < declinedEvents.length - 1 && (
              <div className={styles.eventDivider} />
            )}
          </React.Fragment>
        ))}
      </div>
    </Box>
  );
};
DeclinedEventsPopover.displayName = "DeclinedEventsPopover";
